import { Box, Stack } from '@mui/material'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useRouteAccess } from '@omnea-app/fe-authz/useRouteAccess'
import { SidebarList, SidebarListItemButton, SidebarListItemText, SidebarSectionTitle } from '../StyledSidebarComponents'
import { filterSectionsBySearch } from './filterSectionsBySearch'
import { hrefOfMenuItem, isMenuItemSelected, isMenuItemVisible, recordHrefsOf, type SidebarSection } from './sectionItem'
import { SidebarSearchField } from './SidebarSearchField'

const PREVENT_BROWSER_URL_TOOLTIP_OVERLAP_PADDING = 14

type SidebarSectionsProps = {
  sections: readonly SidebarSection[]
  searchPlaceholder: string
}

export const SidebarSections = ({ sections, searchPlaceholder }: SidebarSectionsProps) => {
  const { t } = useTranslation('common')
  const router = useRouter()
  const [searchQuery, setSearchQuery] = useState('')
  const routeAccess = useRouteAccess()

  const permissionFilteredSections = sections
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => isMenuItemVisible(item, routeAccess)),
    }))
    .filter((section) => section.items.length > 0)

  const filteredSections = filterSectionsBySearch(permissionFilteredSections, searchQuery)
  // Unfiltered: a record owns its address whether or not a search, or a slow load, is hiding its row.
  const recordHrefs = recordHrefsOf(sections)

  return (
    <Stack spacing={3} sx={{ height: '100%', overflow: 'hidden' }}>
      <Stack
        spacing={2}
        sx={{
          pb: PREVENT_BROWSER_URL_TOOLTIP_OVERLAP_PADDING,
          overflowY: 'auto',
          overflowX: 'hidden',
        }}
      >
        <SidebarSearchField placeholder={searchPlaceholder} searchQuery={searchQuery} onSearchChange={setSearchQuery} />
        {filteredSections.map((section) => (
          <Box key={section.title}>
            <SidebarSectionTitle>{section.title}</SidebarSectionTitle>
            <SidebarList>
              {section.items.map((item) => {
                const href = hrefOfMenuItem(item)
                const newTabProps = item.destination.kind === 'external' ? { target: '_blank', rel: 'noopener noreferrer' } : {}
                return (
                  <Link key={href} href={href} passHref {...newTabProps}>
                    <SidebarListItemButton selected={isMenuItemSelected(item, router.asPath, recordHrefs)}>
                      <Box sx={{ minWidth: 24, color: 'fg.muted', mr: 2, mt: 1 }}>{item.icon}</Box>
                      <SidebarListItemText
                        primary={item.label}
                        secondary={item.showsOmneanOnlyBadge === true ? t('sidebar.omnean-only') : undefined}
                      />
                    </SidebarListItemButton>
                  </Link>
                )
              })}
            </SidebarList>
          </Box>
        ))}
      </Stack>
    </Stack>
  )
}
